import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Advantage {
  icon: string;
  title: string;
  description: string;
  stat: string;
}

const WhyChooseUs = () => {
  const advantages: Advantage[] = [
    {
      icon: 'CurrencyDollarIcon',
      title: 'Premium Pricing',
      description: 'Our direct sales model eliminates auction fees and middlemen, passing savings directly to you. Get 15-25% more than typical auction prices.',
      stat: '15-25% Higher'
    },
    {
      icon: 'ClockIcon',
      title: 'Lightning Fast',
      description: 'No waiting for auction dates or buyer financing. Get valuations in 24-48 hours and close deals on your schedule.',
      stat: '24-48 Hours'
    },
    {
      icon: 'ShieldCheckIcon',
      title: 'Zero Risk',
      description: 'Fully licensed, bonded, and insured. Every transaction is protected with comprehensive documentation and secure payment processing.',
      stat: '100% Protected'
    },
    {
      icon: 'UserGroupIcon',
      title: 'Expert Guidance',
      description: 'Work with dedicated fleet specialists who understand commercial vehicle values and market timing to maximize your returns.',
      stat: '20+ Years Experience'
    },
    {
      icon: 'DocumentTextIcon',
      title: 'Complete Transparency',
      description: 'Detailed valuation reports showing exactly how we arrived at our offer. No hidden fees, no surprises, just honest pricing.',
      stat: 'Zero Hidden Fees'
    },
    {
      icon: 'TruckIcon',
      title: 'Hassle-Free Process',
      description: 'We handle all paperwork, pickup logistics, and title transfers. You focus on your business while we handle the details.',
      stat: 'Full Service'
    }
  ];

  return (
    <section className="bg-background py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Why Fleet Managers Choose Direct Sales
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Skip the auction uncertainty and hidden costs. Our transparent, relationship-driven approach delivers superior results every time.
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {advantages.map((advantage, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-6 hover:shadow-brand transition-all duration-200 border border-border hover:border-primary/30"
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon name={advantage.icon as any} size={28} variant="outline" className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-bold text-foreground">{advantage.title}</h3>
                    <span className="text-xs font-semibold text-accent bg-accent/10 px-2 py-1 rounded-full whitespace-nowrap ml-2">
                      {advantage.stat}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {advantage.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison CTA */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
            See the Difference for Yourself
          </h3>
          <p className="text-base text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            Compare our direct sales approach with traditional auctions. The numbers speak for themselves.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/why-choose-direct-sale"
              className="px-8 py-4 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-1 shadow-brand inline-flex items-center justify-center space-x-2"
            >
              <span>View Detailed Comparison</span>
              <Icon name="ChartBarIcon" size={20} variant="outline" />
            </Link>
            <Link
              href="/contact"
              className="px-8 py-4 bg-primary-foreground/10 backdrop-blur-sm text-primary-foreground font-semibold rounded-lg hover:bg-primary-foreground/20 transition-all duration-200 ease-out border border-primary-foreground/20 inline-flex items-center justify-center space-x-2"
            >
              <span>Talk to a Specialist</span>
              <Icon name="PhoneIcon" size={20} variant="outline" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;